import {type ComponentMetadata} from '@webflow/data-types';
import type {Plugins} from '@packages/systems/wfdl';
import type {
  CodeComponentTemplateId,
  CodeComponentTemplateBranding,
  LocalLibraryDescriptor,
  LocalOverrideSource,
} from '@packages/systems/code-libraries/core';
import {
  CreateSandboxResponse,
  DesignerSourceFile,
  type SourceFileKey,
} from '@packages/domains/code-libraries/data-types';
import {
  ASSISTANT_OPENED,
  REMOTE_FILESYSTEM_FILES_COMMITTED,
} from '@packages/systems/designer/actions';
import {type SitePluginActions} from '@packages/systems/site-plugin-designer';
import {ActionCreator, createAction} from '@packages/utilities/create-action';
import type {ImageUploadResult} from '@packages/utilities/image-upload/shared';
import type {MetadataPatch} from '../metadata/applyMetadataToSource';
import type {
  InstantApplyRecord,
  VisualEditsMessageSummary,
} from './summarizeVisualEditsForChat';
import {
  CODE_BUILD_FAILED,
  CODE_BUILD_STARTED,
  CODE_BUILD_SUCCEEDED,
  CODE_DEPENDENCIES_INSTALLATION_FINISHED,
  CODE_COMPONENT_CREATED,
  CODE_COMPONENT_TEMPLATE_DRAG_STARTED,
  CODE_COMPONENT_TEMPLATE_WELCOME_SHOWN,
  CODE_COMPONENT_CREATOR_DISMISSED,
  CODE_COMPONENT_CREATOR_PROMPT_SET,
  CODE_COMPONENT_CLARIFICATION_STARTED,
  CODE_COMPONENT_CLARIFICATION_ENDED,
  CODE_COMPONENT_SOURCE_DRAFT_CHANGED,
  CODE_COMPONENT_SOURCE_DRAFTS_CLEARED,
  CODE_COMPONENT_SOURCE_FILE_SAVE_REFUSED,
  CODE_COMPONENT_EDITOR_PANE_HIDE,
  CODE_COMPONENT_EDITOR_PANE_MOVE,
  CODE_COMPONENT_EDITOR_PANE_RESIZE,
  CODE_COMPONENT_EDITOR_PANE_SHOW,
  CODE_COMPONENT_SOURCE_FILE_SELECTED,
  CODE_COMPONENT_SOURCE_TREE_COLLAPSED,
  CODE_COMPONENT_RELOADED_FROM_CRASH,
  CODE_COMPONENT_UPDATED,
  CODE_EVALUATED,
  CODE_EVALUATION_FAILED,
  COMPONENT_PREVIEW_SCENARIOS_DEFINED,
  COMPONENT_PREVIEW_SCENARIO_REMOVED,
  COMPONENT_PREVIEW_SCENARIO_EDITED,
  COMPONENT_PREVIEW_SCENARIO_CREATED,
  CODE_LIBRARIES_DISABLE_SAFE_MODE_FAILED,
  CODE_LIBRARIES_DISABLE_SAFE_MODE_REQUESTED,
  CODE_LIBRARIES_DISABLE_SAFE_MODE_SUCCEEDED,
  INSTRUCTION_SOURCE_FILE_UPSERTED,
  VCC_SOURCE_FILE_CONTENT_SAVED,
  VCC_SOURCE_FILE_CREATED,
  VCC_SOURCE_FILE_RENAMED,
  VCC_SOURCE_FILE_DELETED,
  VCC_UNUSED_SOURCE_FILES_COLLECTED,
  INSTRUCTION_SOURCE_FILES_RECONCILED,
  INSTRUCTION_SUBTREE_RENAMED,
  INSTRUCTION_SUBTREE_DELETED,
  VCC_STYLE_OVERRIDES_UPDATED,
  VCC_STYLE_OVERRIDES_CLEARED,
  VCC_STYLE_OVERRIDE_GROUP_REMOVED,
  VCC_STYLE_OVERRIDES_IDENTITY_DROPPED,
  VCC_STYLE_OVERRIDES_INSTANT_APPLIED,
  VCC_STYLE_OVERRIDES_INSTANT_APPLY_PENDING,
  VCC_PROP_CONNECTIONS_RESOLVED,
  VCC_SELECTED_ELEMENT_REVEALED,
  VCC_DRAFT_STYLES_APPLIED,
  VCC_TEXT_OVERRIDES_UPDATED,
  VCC_TEXT_OVERRIDES_CLEARED,
  VCC_TEXT_OVERRIDE_GROUP_REMOVED,
  VCC_TEXT_EDIT_STARTED,
  VCC_TEXT_EDIT_ENDED,
  VCC_INSTANT_TEXT_APPLIED,
  VCC_INSTANT_APPLY_REPORTED,
  VCC_INSTANT_APPLY_ROLLED_BACK,
  VCC_APPLY_CANCELLED,
  VCC_APPLY_SOURCE_SNAPSHOT_DROPPED,
  VCC_APPLY_UNDO_TURN_ATTACHED,
  VCC_APPLY_SOURCE_SNAPSHOT_TAKEN,
  VISUAL_EDITING_ONBOARDING_ADVANCED,
  VISUAL_EDITING_ONBOARDING_DISMISSED,
  VISUAL_EDITING_ONBOARDING_GENERATION_STARTED,
  VISUAL_EDITING_ONBOARDING_STARTED,
  REMOTE_SANDBOX_CREATED,
  REMOTE_SANDBOX_EXPIRED,
  REMOTE_SANDBOX_FILES_DELETED,
  REMOTE_SANDBOX_INITIATED,
  REMOTE_SANDBOX_SYNC_COMPLETED,
  REMOTE_SANDBOX_SYNC_STARTED,
  FUNCTIONS_PREVIEW_CONNECTING,
  FUNCTIONS_PREVIEW_CONNECTED,
  FUNCTIONS_PREVIEW_FAILED,
  FUNCTIONS_PREVIEW_EMPTY,
  FUNCTIONS_PREVIEW_DISABLED,
  FUNCTIONS_GENERATION_MISSING,
  LOCAL_DEV_SERVER_REACHABILITY_CHANGED,
  LOCAL_DEV_LIBRARY_DISCOVERED,
  LOCAL_OVERRIDE_OPT_OUT_CHANGED,
  LOCAL_OVERRIDE_ADDED,
  LOCAL_OVERRIDE_REMOVED,
} from './actionTypes';
import type {FunctionsPreview} from './types';
import type {ComponentStateScenario} from '@packages/systems/code-libraries/component-states';
import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import type {UserMessageContextTag} from '@packages/systems/ai/assistant-next/frontend';
import type {
  AIAssistantOpenSource,
  PasteAttachment,
} from '@packages/domains/ai-assistant/data-types';

export type VirtualFileSystem = {
  '/theme.css': string;
  '/index.ts': string;
} & {
  [key: string]: string;
};

/**
 * Safe mode action creators
 */
export const codeLibrariesDisableSafeModeRequested: ActionCreator<
  typeof CODE_LIBRARIES_DISABLE_SAFE_MODE_REQUESTED,
  void
> = createAction(CODE_LIBRARIES_DISABLE_SAFE_MODE_REQUESTED);

export const codeLibrariesDisableSafeModeSucceeded: ActionCreator<
  typeof CODE_LIBRARIES_DISABLE_SAFE_MODE_SUCCEEDED,
  void
> = createAction(CODE_LIBRARIES_DISABLE_SAFE_MODE_SUCCEEDED);

export const codeLibrariesDisableSafeModeFailed: ActionCreator<
  typeof CODE_LIBRARIES_DISABLE_SAFE_MODE_FAILED,
  void
> = createAction(CODE_LIBRARIES_DISABLE_SAFE_MODE_FAILED);

/**
 * Component Map action creators
 */
export const codeComponentCreated: ActionCreator<
  typeof CODE_COMPONENT_CREATED,
  {
    componentId: string;
    entryKind: 'blank';
    destination?: 'page';
    templateId?: CodeComponentTemplateId;
    templateBranding?: CodeComponentTemplateBranding;
  }
> = createAction(CODE_COMPONENT_CREATED);

export type CodeComponentTemplateDrag = {
  componentId: string;
  templateId: CodeComponentTemplateId;
  templateBranding: CodeComponentTemplateBranding;
};

export const codeComponentTemplateDragStarted = createAction<
  typeof CODE_COMPONENT_TEMPLATE_DRAG_STARTED,
  CodeComponentTemplateDrag
>(CODE_COMPONENT_TEMPLATE_DRAG_STARTED);

export const codeComponentTemplateWelcomeShown: ActionCreator<
  typeof CODE_COMPONENT_TEMPLATE_WELCOME_SHOWN,
  {componentId: string}
> = createAction(CODE_COMPONENT_TEMPLATE_WELCOME_SHOWN);

export const codeComponentUpdated: ActionCreator<
  typeof CODE_COMPONENT_UPDATED,
  {
    code: string;
    componentId: string;
    metadataEdit?: {expectedSource: string; changes: MetadataPatch};
  }
> = createAction(CODE_COMPONENT_UPDATED);

export const codeBuildStarted: ActionCreator<typeof CODE_BUILD_STARTED, void> =
  createAction(CODE_BUILD_STARTED);

export const codeBuildSucceeded: ActionCreator<
  typeof CODE_BUILD_SUCCEEDED,
  {
    js: string;
    css: string;
    functionsHash?: string;
    sourceRevisions?: Readonly<Record<string, string>>;
  }
> = createAction(CODE_BUILD_SUCCEEDED);

export const codeBuildFailed: ActionCreator<typeof CODE_BUILD_FAILED, string> =
  createAction(CODE_BUILD_FAILED);

export const codeDependenciesInstallationFinished: ActionCreator<
  typeof CODE_DEPENDENCIES_INSTALLATION_FINISHED,
  {recoveredNames: string[]}
> = createAction(CODE_DEPENDENCIES_INSTALLATION_FINISHED);

export const codeEvaluated: ActionCreator<
  typeof CODE_EVALUATED,
  {[key: string]: ComponentMetadata & {sourceRevision?: string}}
> = createAction(CODE_EVALUATED);

export const codeEvaluationFailed: ActionCreator<
  typeof CODE_EVALUATION_FAILED,
  string
> = createAction(CODE_EVALUATION_FAILED);

/**
 * Sets the Designer-only multi-state preview scenarios for a AI Code Component.
 */
export const componentPreviewScenariosDefined: ActionCreator<
  typeof COMPONENT_PREVIEW_SCENARIOS_DEFINED,
  {
    componentId: string;
    scenarios: readonly ComponentStateScenario[];
    analytics?: {conversationId: string; turnId: string; toolCallId: string};
  }
> = createAction(COMPONENT_PREVIEW_SCENARIOS_DEFINED);

/**
 * Manually removes a single Designer-only multi-state preview scenario from a
 * AI Code Component. Unlike `componentPreviewScenariosDefined` (a full replace
 * that re-stamps the states' source hash), this preserves the recorded
 * staleness of the remaining states, since a manual removal doesn't regenerate
 * them against the current code.
 */
export const componentPreviewScenarioRemoved: ActionCreator<
  typeof COMPONENT_PREVIEW_SCENARIO_REMOVED,
  {componentId: string; scenarioId: string}
> = createAction(COMPONENT_PREVIEW_SCENARIO_REMOVED);

/**
 * Manually edits a single Designer-only multi-state preview scenario in place.
 * The scenario keeps its `id` (so canvas grid frames and scenario bindings stay
 * stable); only its `label` and `props` change. Like
 * `componentPreviewScenarioRemoved`, this preserves the surviving states'
 * source hash — a manual edit doesn't regenerate them against the current code,
 * so their staleness is unchanged. `props` omitted clears the scenario's props.
 */
export const componentPreviewScenarioEdited: ActionCreator<
  typeof COMPONENT_PREVIEW_SCENARIO_EDITED,
  {
    componentId: string;
    scenarioId: string;
    label: string;
    props?: Readonly<Record<string, unknown>>;
  }
> = createAction(COMPONENT_PREVIEW_SCENARIO_EDITED);

/**
 * Manually creates a single Designer-only multi-state preview scenario and
 * appends it to the component's existing states. The `scenarioId` is generated
 * client-side (hidden from the user) and must be unique on the component. Like
 * `componentPreviewScenarioRemoved` / `componentPreviewScenarioEdited`, this
 * preserves the surviving states' source hash — a manual creation doesn't
 * regenerate states against the current code, so their staleness is unchanged.
 * `props` omitted creates the scenario without props.
 */
export const componentPreviewScenarioCreated: ActionCreator<
  typeof COMPONENT_PREVIEW_SCENARIO_CREATED,
  {
    componentId: string;
    scenarioId: string;
    label: string;
    props?: Readonly<Record<string, unknown>>;
  }
> = createAction(COMPONENT_PREVIEW_SCENARIO_CREATED);

export const codeComponentReloadedFromCrash: ActionCreator<
  typeof CODE_COMPONENT_RELOADED_FROM_CRASH,
  void
> = createAction(CODE_COMPONENT_RELOADED_FROM_CRASH);

/**
 * Code Component Editor state action creators
 */

/** `key` is a `sourceFileKey`, or `MAIN_SOURCE_DRAFT_KEY` for the component's
 * own source. A `content` of `null` drops the draft, marking the file clean. */
export type CodeComponentSourceDraftChangedPayload = {
  key: string;
  content: string | null;
};
export const codeComponentSourceDraftChanged: ActionCreator<
  typeof CODE_COMPONENT_SOURCE_DRAFT_CHANGED,
  CodeComponentSourceDraftChangedPayload
> = createAction(CODE_COMPONENT_SOURCE_DRAFT_CHANGED);

export const codeComponentSourceDraftsCleared: ActionCreator<
  typeof CODE_COMPONENT_SOURCE_DRAFTS_CLEARED,
  void
> = createAction(CODE_COMPONENT_SOURCE_DRAFTS_CLEARED);

/**
 * Files whose save the write rules refused, which the editor keeps showing a
 * reason for until each one would save.
 *
 * Every dispatch bumps `sourceFileSaveRefusalVersion`, even when every key is
 * already listed: a repeated refusal of the same file is still an event the
 * editor has to react to, and the keys alone cannot say one happened.
 */
export type CodeComponentSourceFileSaveRefusedPayload = {
  keys: readonly string[];
};
export const codeComponentSourceFileSaveRefused: ActionCreator<
  typeof CODE_COMPONENT_SOURCE_FILE_SAVE_REFUSED,
  CodeComponentSourceFileSaveRefusedPayload
> = createAction(CODE_COMPONENT_SOURCE_FILE_SAVE_REFUSED);

/**
 * Code Component Editor popover action creators
 */
export const codeComponentEditorPaneShow: ActionCreator<
  typeof CODE_COMPONENT_EDITOR_PANE_SHOW,
  void
> = createAction(CODE_COMPONENT_EDITOR_PANE_SHOW);

export const codeComponentEditorPaneHide: ActionCreator<
  typeof CODE_COMPONENT_EDITOR_PANE_HIDE,
  void
> = createAction(CODE_COMPONENT_EDITOR_PANE_HIDE);

export const codeComponentEditorPaneMove: ActionCreator<
  typeof CODE_COMPONENT_EDITOR_PANE_MOVE,
  {x: number; y: number}
> = createAction(CODE_COMPONENT_EDITOR_PANE_MOVE);

export const codeComponentEditorPaneResize: ActionCreator<
  typeof CODE_COMPONENT_EDITOR_PANE_RESIZE,
  {width: number; height: number}
> = createAction(CODE_COMPONENT_EDITOR_PANE_RESIZE);

/**
 * Selects which source file the editor displays. `null` returns the editor to
 * the canvas-focused component's own source (the default, single-file view).
 * Payload is a `sourceFileKey` (`${folderId ?? ''}/${name}`).
 */
export const codeComponentSourceFileSelected: ActionCreator<
  typeof CODE_COMPONENT_SOURCE_FILE_SELECTED,
  string | null
> = createAction(CODE_COMPONENT_SOURCE_FILE_SELECTED);

/**
 * Collapses or expands the editor's source-file tree. Session-only: the flag is
 * not persisted, so the tree opens with the editor.
 */
export const codeComponentSourceTreeCollapsed: ActionCreator<
  typeof CODE_COMPONENT_SOURCE_TREE_COLLAPSED,
  boolean
> = createAction(CODE_COMPONENT_SOURCE_TREE_COLLAPSED);

/**
 * Where a "submit drafted style/text changes to the AI Assistant" action was
 * initiated from. Reported as the `source` property on the
 * `VCC Visual Edit Submitted` analytics event.
 * - `style_panel`: the dedicated "Send to AI Assistant" button in the VCC style
 *   panel.
 * - `assistant_chat`: submitting drafts from the AIA chat composer.
 * - `unfocus_modal`: the unsaved-drafts confirmation modal shown when the user
 *   attempts to leave the focused code-component view.
 * - `auto`: drafts flushed by another flow (e.g. publish, page navigation).
 */
export type VccVisualEditSource =
  | 'style_panel'
  | 'assistant_chat'
  | 'unfocus_modal'
  | 'auto';

export type PendingCreatorPrompt = {
  text: string;
  images?: ImageUploadResult[];
  fromSuggestion?: boolean;
  /**
   * True when the prompt came from the AICC create form
   * (`codeComponentCreatorDismissed`). Edit-time hand-offs via
   * `codeComponentCreatorPromptSet` leave this unset; auto-send then sets
   * `aiccClarification: false` so the server skips the clarification prompt
   * (omitting the key would still enable it).
   */
  fromCreator?: boolean;
  contextTags?: UserMessageContextTag[];
  /** Large pastes (EXT-303) collapsed into chips on the creator's first message. */
  pasteAttachments?: PasteAttachment[];
  /** Set when the prompt applies drafted visual edits; drives analytics `source`. */
  source?: VccVisualEditSource;
  /**
   * Drives `assistant_open_source` on `VCC Prompt Entered`. Threaded rather
   * than read from the store at submit time: auto-send re-dispatches
   * `ASSISTANT_OPENED` first, overwriting the parking surface's source.
   */
  assistantOpenSource?: AIAssistantOpenSource;
  /** The confirmed batch, overriding what the send would derive (EXT-2020). */
  visualEdits?: VisualEditsMessageSummary;
  /** What that same click already wrote, for the turn's message to carry. */
  instantApply?: InstantApplyRecord;
};

export const codeComponentCreatorDismissed: ActionCreator<
  typeof CODE_COMPONENT_CREATOR_DISMISSED,
  PendingCreatorPrompt
> = createAction(CODE_COMPONENT_CREATOR_DISMISSED);

export const codeComponentCreatorPromptSet: ActionCreator<
  typeof CODE_COMPONENT_CREATOR_PROMPT_SET,
  PendingCreatorPrompt | null
> = createAction(CODE_COMPONENT_CREATOR_PROMPT_SET);

export const codeComponentClarificationStarted: ActionCreator<
  typeof CODE_COMPONENT_CLARIFICATION_STARTED,
  void
> = createAction(CODE_COMPONENT_CLARIFICATION_STARTED);

export const codeComponentClarificationEnded: ActionCreator<
  typeof CODE_COMPONENT_CLARIFICATION_ENDED,
  void
> = createAction(CODE_COMPONENT_CLARIFICATION_ENDED);

/**
 * Local creator for the cross-store ASSISTANT_OPENED action.
 *
 * The canonical creator lives in `@packages/systems/ai/assistant-next/frontend`,
 * but importing it here would pull the entire assistant-next barrel — including
 * `AssistantChat` and its ESM-only `rehype-raw` dependency — into Jest's
 * CommonJS module graph, breaking unrelated test suites. Re-declaring the
 * creator locally keeps the dispatch path narrow: same action type, same
 * payload shape, same reducer in `AIAssistantStore`.
 */
export const aiAssistantOpened: ActionCreator<
  typeof ASSISTANT_OPENED,
  {clearConversation?: boolean; clearCmsCollections?: boolean} | undefined
> = createAction(ASSISTANT_OPENED);

// The build-time identity every occurrence of a source element shares across
// `(variant × scenario)` islands: its `data-wf-loc-text` stamp plus the
// `data-wf-src-rev` of the built file that rendered it.
export type VCCStyleSourceIdentity = {
  stamp: string;
  rev: string;
};

export type VCCStyleEntry = {
  nodeId: string;
  nativeIdPath: string[];
  tagName: string;
  textContent: string;
  original: string;
  modified: string;
  variant?: {id: string; displayName: string};
  /**
   * Set when the draft was authored inside a multi-state preview scenario
   * frame (see `selectedCodeComponentInnerNode.componentState`). Lets the
   * AI Assistant scope the edit to the right JSX branch. With a
   * `sourceIdentity` the entry is shared across scenarios, so this is
   * authoring context only, not part of the draft's scope.
   */
  componentState?: {id: string; label: string};
  breakpointId: string;
  /** When set, overrides are for this pseudo state (default / none is stored without `::p:` in the map key). */
  pseudoState?: PseudoStateKey | null;
  // Present when the entry is keyed by source identity instead of nodeId + scenario.
  sourceIdentity?: VCCStyleSourceIdentity;
  hasConnectedProp?: boolean;
  propConnectionRevision?: string;
};

export type VCCStyleOverridesMap = Record<string, VCCStyleEntry>;

export function getStyleOverrideKey(
  nodeId: string,
  variantId?: string,
  breakpointId?: string,
  pseudoState?: PseudoStateKey | null,
  scenarioId?: string
): string {
  let key = nodeId;
  // Scenario id comes first after the node so per-state drafts at the
  // same path bucket independently; variant / breakpoint / pseudo all
  // compose on top of it.
  if (scenarioId) key += `::s:${scenarioId}`;
  if (variantId) key += `::${variantId}`;
  if (breakpointId) key += `::${breakpointId}`;
  if (pseudoState) key += `::p:${pseudoState}`;
  return key;
}

// The `@src:` prefix keeps source-identity keys distinct from node-id keys.
export const SOURCE_STYLE_OVERRIDE_KEY_BASE_PREFIX = '@src:';

export function getSourceStyleOverrideKeyBase(
  identity: VCCStyleSourceIdentity
): string {
  return `${SOURCE_STYLE_OVERRIDE_KEY_BASE_PREFIX}${identity.stamp}@${identity.rev}`;
}

// The identity a draft is bookkept by, independent of variant / breakpoint /
// pseudo. Paint records and survivor sets are keyed by it.
export function getStyleOverrideEntryIdentity(entry: {
  nodeId: string;
  sourceIdentity?: VCCStyleSourceIdentity;
}): string {
  return entry.sourceIdentity
    ? getSourceStyleOverrideKeyBase(entry.sourceIdentity)
    : entry.nodeId;
}

// The override map key for a stamped source element — deliberately no scenario,
// so one entry serves every `(variant × scenario)` frame.
export function getSourceStyleOverrideKey(
  identity: VCCStyleSourceIdentity,
  variantId?: string,
  breakpointId?: string,
  pseudoState?: PseudoStateKey | null
): string {
  return getStyleOverrideKey(
    getSourceStyleOverrideKeyBase(identity),
    variantId,
    breakpointId,
    pseudoState
  );
}

/**
 * Drop a pre-variant-resolution entry once a variant id is known.
 *
 * `vccStyleOverridesUpdated` can fire before the active frame's variant has
 * resolved, so the first edit on a node lands under the variant-less key
 * `keyBase::bp[::pseudo]`. A later dispatch (a follow-up keystroke, or the
 * re-key effect in `VCCStylePanel`) arrives with the variant id and would
 * store a second entry under `keyBase::variantId::bp[::pseudo]`, duplicating
 * the same logical override in the Style Changes list. This trims the
 * variant-less entry when the canonical (variant-keyed) entry takes over.
 *
 * `keyBase` is the `nodeId` for legacy entries and
 * [[getSourceStyleOverrideKeyBase]] for source-identity entries; `scenarioId`
 * is supplied only in the legacy case.
 */
export function withoutLegacyVariantlessOverride(
  overrides: VCCStyleOverridesMap,
  keyBase: string,
  breakpointId: string,
  pseudoState: PseudoStateKey | null | undefined,
  canonicalKey: string,
  scenarioId?: string
): VCCStyleOverridesMap {
  const legacyKey = getStyleOverrideKey(
    keyBase,
    undefined,
    breakpointId,
    pseudoState,
    scenarioId
  );
  if (legacyKey === canonicalKey || !(legacyKey in overrides)) {
    return overrides;
  }
  const {[legacyKey]: _removed, ...rest} = overrides;
  return rest;
}

export const vccStyleOverridesUpdated: ActionCreator<
  typeof VCC_STYLE_OVERRIDES_UPDATED,
  {
    nodeId: string;
    nativeIdPath: string[];
    tagName: string;
    textContent: string;
    original: string;
    modified: string;
    variant?: {id: string; displayName: string};
    componentState?: {id: string; label: string};
    breakpointId: string;
    pseudoState?: PseudoStateKey | null;
    // Set when the element carries a build stamp; selects the source-identity key.
    sourceIdentity?: VCCStyleSourceIdentity;
  }
> = createAction(VCC_STYLE_OVERRIDES_UPDATED);

export const vccPropConnectionsResolved: ActionCreator<
  typeof VCC_PROP_CONNECTIONS_RESOLVED,
  {revision: string; connections: Record<string, boolean>}
> = createAction(VCC_PROP_CONNECTIONS_RESOLVED);

export const vccStyleOverridesCleared: ActionCreator<
  typeof VCC_STYLE_OVERRIDES_CLEARED,
  void
> = createAction(VCC_STYLE_OVERRIDES_CLEARED);

export const vccStyleOverrideGroupRemoved: ActionCreator<
  typeof VCC_STYLE_OVERRIDE_GROUP_REMOVED,
  {nodeId: string; variantId?: string}
> = createAction(VCC_STYLE_OVERRIDE_GROUP_REMOVED);

// Drops identity-keyed drafts while the cascade gate is off; only keys whose
// entry still carries a `sourceIdentity` are removed.
export const vccStyleOverridesIdentityDropped: ActionCreator<
  typeof VCC_STYLE_OVERRIDES_IDENTITY_DROPPED,
  readonly string[]
> = createAction(VCC_STYLE_OVERRIDES_IDENTITY_DROPPED);

/**
 * Instant CSS apply (EXT-1219) wrote these drafts into source and is waiting on the
 * rebuild. Carries the entries, not just keys, so `CODE_EVALUATED` can drop only the
 * ones the user hasn't touched since.
 */
export const vccStyleOverridesInstantApplied: ActionCreator<
  typeof VCC_STYLE_OVERRIDES_INSTANT_APPLIED,
  VCCStyleOverridesMap
> = createAction(VCC_STYLE_OVERRIDES_INSTANT_APPLIED);

/**
 * Keys the armed instant-apply batch (EXT-1219) expects to write, replacing the last
 * guess; an empty payload means the batch settled. A guess, not a record — the AST
 * fold can still decline a key, so read it as "not the assistant's problem yet".
 */
export const vccStyleOverridesInstantApplyPending: ActionCreator<
  typeof VCC_STYLE_OVERRIDES_INSTANT_APPLY_PENDING,
  readonly string[]
> = createAction(VCC_STYLE_OVERRIDES_INSTANT_APPLY_PENDING);

/**
 * The source lines rendering the element just selected on the canvas (EXT-870), or
 * `null` when nothing is selected or the element has no reachable stamp.
 *
 * Separate from `vccInstantTextApplied` on purpose: that one means "these lines just
 * changed" and flashes accordingly, which would be both noisy and untrue on every
 * canvas click. This one drives a scroll plus a quiet persistent tint.
 */
export const vccSelectedElementRevealed: ActionCreator<
  typeof VCC_SELECTED_ELEMENT_REVEALED,
  VCCInstantApplyLineRange | null
> = createAction(VCC_SELECTED_ELEMENT_REVEALED);

export const vccDraftStylesApplied: ActionCreator<
  typeof VCC_DRAFT_STYLES_APPLIED,
  void
> = createAction(VCC_DRAFT_STYLES_APPLIED);

export type VccTextStyleProperty =
  | 'font-family'
  | 'font-weight'
  | 'font-size'
  | 'color';

export type TextDraftAttributes = Partial<
  Record<VccTextStyleProperty, string>
> & {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  superscript?: boolean;
  subscript?: boolean;
  code?: boolean;
};

export type TextDraftOp = {
  insert: string;
  attributes?: TextDraftAttributes;
};

export type TextDraftContent =
  | {kind: 'plain'; text: string}
  | {kind: 'rich'; ops: TextDraftOp[]};

/**
 * Source location of an edited element's text content, in the coordinates of
 * the stored component `source.code` (already rebased past the projected
 * globals prefix). Captured from the build-stamped `data-wf-loc-text` attribute and
 * used by the instant-apply engine to splice the precise spot with no
 * ambiguity. Optional: absent for older drafts and whenever no stamp was
 * present, in which case the engine falls back to text matching.
 * Line is 1-based, column 0-based (Babel `loc` convention).
 */
export type VCCSourceLoc = {
  line: number;
  col: number;
  endLine: number;
  endCol: number;
};

/**
 * Everything the build stamped onto (and around) the edited element, read at
 * double-click time and rebased to `source.code` coordinates.
 *
 * `rev` is the content hash of the source revision the coordinates belong to
 * (`data-wf-src-rev`); the engine recomputes it over the current source and
 * ignores the whole stamp on mismatch, so stale coordinates can never target
 * the wrong spot. `index`/`count` locate the element among all elements
 * carrying the *same* stamp in the rendered DOM — more than one means the
 * element renders inside a loop, and `index` is the loop iteration, which the
 * engine uses to resolve data-driven text back to its array literal.
 * `childSpans` are the source spans of the element's static text runs
 * (`data-wf-loc-kids`), present only for mixed content.
 */
export type VCCSourceStamp = {
  rev: string;
  loc: VCCSourceLoc;
  index: number;
  count: number;
  childSpans?: VCCSourceLoc[];
};

/**
 * Scopes an inline text edit to a single direct text run of a mixed-content
 * element (text interleaved with nested elements/expressions). `index` is the
 * run's position among the element's direct meaningful text runs in DOM order,
 * `count` the total at session start. The draft stays addressed by the
 * element's stable node id — the run scope travels alongside it — so nothing
 * outside the live editing session depends on the transient wrapper the editor
 * uses to confine `contenteditable` to the run.
 */
export type VCCTextRunScope = {
  index: number;
  count: number;
};

type VCCTextEntry = {
  nodeId: string;
  nativeIdPath: string[];
  tagName: string;
  original: TextDraftContent;
  modified: TextDraftContent;
  variant?: {id: string; displayName: string};
  /**
   * Set when the inline text edit was started inside a multi-state
   * preview scenario frame. Mirrors the same field on `VCCStyleEntry`.
   */
  componentState?: {id: string; label: string};
  sourceStamp?: VCCSourceStamp;
  runScope?: VCCTextRunScope;
  hasConnectedProp?: boolean;
  propConnectionRevision?: string;
};

export type VCCTextOverridesMap = Record<string, VCCTextEntry>;

export function getTextOverrideKey({
  nodeId,
  variantId,
  runIndex,
  scenarioId,
}: {
  nodeId: string;
  variantId?: string;
  runIndex?: number;
  scenarioId?: string;
}): string {
  let base = nodeId;
  if (scenarioId) base += `::s:${scenarioId}`;
  if (variantId) base += `::${variantId}`;
  if (runIndex !== undefined) base += `::run${runIndex}`;
  return base;
}

export const vccTextOverridesUpdated: ActionCreator<
  typeof VCC_TEXT_OVERRIDES_UPDATED,
  {
    nodeId: string;
    nativeIdPath: string[];
    tagName: string;
    original: TextDraftContent;
    modified: TextDraftContent;
    variant?: {id: string; displayName: string};
    sourceStamp?: VCCSourceStamp;
    runScope?: VCCTextRunScope;
    componentState?: {id: string; label: string};
  }
> = createAction(VCC_TEXT_OVERRIDES_UPDATED);

export const vccTextOverridesCleared: ActionCreator<
  typeof VCC_TEXT_OVERRIDES_CLEARED,
  void
> = createAction(VCC_TEXT_OVERRIDES_CLEARED);

export const vccTextOverrideGroupRemoved: ActionCreator<
  typeof VCC_TEXT_OVERRIDE_GROUP_REMOVED,
  // `runIndex` narrows the removal to one run-scoped draft (set when the
  // instant apply consumed exactly that run's edit); omitted, the whole
  // node/variant group goes — the discard-UI semantics. `scenarioId` narrows it
  // to one preview state's draft; omitted, every state's draft goes.
  {nodeId: string; variantId?: string; runIndex?: number; scenarioId?: string}
> = createAction(VCC_TEXT_OVERRIDE_GROUP_REMOVED);

// Lives here (not in toggleVccTextFormat.ts, which re-exports it) so the
// editing target can reference it without an import cycle.
export type VccTextFormat =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'superscript'
  | 'subscript'
  | 'code'
  | 'clear';

export type VCCTextEditingTarget = {
  nodeId: string;
  nativeIdPath: string[];
  tagName: string;
  baseline: TextDraftContent;
  variant?: {id: string; displayName: string};
  componentState?: {id: string; label: string};
  selectAllOnEnter?: boolean;
  // Viewport-relative click position used to place the caret after the click
  // starts a new editing session.
  caretPositionOnEnter?: {clientX: number; clientY: number};
  // Only meaningful with `selectAllOnEnter`: the editor applies this format
  // once to a temporary full selection, then ends the session so a format
  // button pressed outside a session does not leave the text selected.
  pendingFormat?: VccTextFormat;
  sourceStamp?: VCCSourceStamp;
  runScope?: VCCTextRunScope;
};

export const vccTextEditStarted: ActionCreator<
  typeof VCC_TEXT_EDIT_STARTED,
  VCCTextEditingTarget
> = createAction(VCC_TEXT_EDIT_STARTED);

export const vccTextEditEnded: ActionCreator<typeof VCC_TEXT_EDIT_ENDED, void> =
  createAction(VCC_TEXT_EDIT_ENDED);

/**
 * 1-based line range (inclusive) of the source region an instant text apply
 * just rewrote, in post-rewrite coordinates. The code editor pane listens and
 * scrolls the range into view with a brief highlight, so the user sees where
 * on the source their canvas edit landed.
 */
export type VCCInstantApplyLineRange = {
  fromLine: number;
  toLine: number;
};

export const vccInstantTextApplied: ActionCreator<
  typeof VCC_INSTANT_TEXT_APPLIED,
  VCCInstantApplyLineRange
> = createAction(VCC_INSTANT_TEXT_APPLIED);

/**
 * What a confirm-time instant apply just wrote, for the surfaces that report the
 * apply back to the user (EXT-1958). `null` when the batch wrote source but could
 * not describe what it wrote, so the report still fires and the surface simply has
 * nothing to list.
 *
 * Separate from `vccInstantTextApplied`, which is the code editor's scroll-and-
 * highlight target: that one also fires on the auto-fire path, carries source
 * coordinates rather than drafts, and says nothing about what the user changed.
 */
export const vccInstantApplyReported: ActionCreator<
  typeof VCC_INSTANT_APPLY_REPORTED,
  {
    summary: VisualEditsMessageSummary | null;
    /**
     * The undo-turn markers bracketing this apply's write, so the chat can match
     * the rebuild it is reporting to the transcript entry the same apply produced
     * (EXT-2020). Empty string when the surface did not bracket the write.
     */
    turnId: string;
  }
> = createAction(VCC_INSTANT_APPLY_REPORTED);

/**
 * A confirm-time instant apply was called off after it had already written
 * (EXT-1976). Cancel undoes the whole apply — the source save is reverted by the
 * caller, and this puts the store back to the queue that produced it: the style
 * drafts stop counting as applied, and the text drafts the batch removed come
 * back. Carries the entries themselves because nothing else still holds them.
 *
 * Keys already re-edited since the apply keep the newer draft: the user's later
 * edit is the one they can see on the canvas.
 */
export const vccInstantApplyRolledBack: ActionCreator<
  typeof VCC_INSTANT_APPLY_ROLLED_BACK,
  {
    style: VCCStyleOverridesMap;
    text: VCCTextOverridesMap;
    /** The queue as it stood at the click, so the reducer does not have to
     * infer it from state a cancel may already have cleared. */
    styleQueuedAtApply: VCCStyleOverridesMap;
    textQueuedAtApply: VCCTextOverridesMap;
  }
> = createAction(VCC_INSTANT_APPLY_ROLLED_BACK);

/**
 * The user called off a pending draft apply (EXT-1976). Dispatched by every
 * surface that lets them stop one — the draft toolbar's Cancel, the assistant
 * panel's Stop, the component-creator shell's Stop — alongside whatever that
 * surface does to the turn itself.
 *
 * Separate from `AI_ASSISTANT_CANCEL_GENERATION` on purpose, in both
 * directions:
 *
 * - It fires even when no turn is in flight. The stuck "Applying changes" bar
 *   the ticket describes is a deferred clear left armed *after* the turn ended,
 *   and the toolbar cannot reach for a turn cancel there — dispatching one would
 *   push a synthetic interrupted marker into a conversation that finished
 *   normally.
 * - Turn cancels that carry no user intent do not fire it. The stream engine's
 *   soft end (`dispatchSoftEnd`) cancels a turn whose transport died, which says
 *   nothing about whether the user wanted the work undone, so it must not drag a
 *   completed instant apply back out of source.
 */
export const vccApplyCancelled: ActionCreator<
  typeof VCC_APPLY_CANCELLED,
  void
> = createAction(VCC_APPLY_CANCELLED);

/**
 * The component's source at the moment of an Apply click, before either engine
 * runs (EXT-1976). Taken on every apply, including one the engines decline
 * outright, because the assistant writes source through its own tools and Stop
 * has to be able to put that back too. See `vccApplySourceSnapshot`.
 */
export const vccApplySourceSnapshotTaken: ActionCreator<
  typeof VCC_APPLY_SOURCE_SNAPSHOT_TAKEN,
  {componentId: string; code: string}
> = createAction(VCC_APPLY_SOURCE_SNAPSHOT_TAKEN);

/**
 * Consumes the snapshot (EXT-1976). Dispatched by the rollback once it has used
 * it, so the snapshot is a one-shot token: a second cancel arriving from another
 * surface finds nothing left and writes no second copy of the same source.
 */
export const vccApplySourceSnapshotDropped: ActionCreator<
  typeof VCC_APPLY_SOURCE_SNAPSHOT_DROPPED,
  void
> = createAction(VCC_APPLY_SOURCE_SNAPSHOT_DROPPED);

/**
 * Names the undo bracket the apply opened around its write (EXT-1976).
 *
 * Separate from taking the snapshot because the bracket does not exist yet at
 * the click — it opens around the write — while the snapshot has to be the
 * source from before it. First one wins, like the source: a two-batch apply's
 * transcript entry keys on the earlier bracket (EXT-2020 folds the retained
 * record), so the id a rollback reverts has to be that same earlier one.
 */
export const vccApplyUndoTurnAttached: ActionCreator<
  typeof VCC_APPLY_UNDO_TURN_ATTACHED,
  {turnId: string}
> = createAction(VCC_APPLY_UNDO_TURN_ATTACHED);

export const visualEditingOnboardingAdvanced: ActionCreator<
  typeof VISUAL_EDITING_ONBOARDING_ADVANCED,
  void
> = createAction(VISUAL_EDITING_ONBOARDING_ADVANCED);

export const visualEditingOnboardingDismissed: ActionCreator<
  typeof VISUAL_EDITING_ONBOARDING_DISMISSED,
  void
> = createAction(VISUAL_EDITING_ONBOARDING_DISMISSED);

export const visualEditingOnboardingGenerationStarted: ActionCreator<
  typeof VISUAL_EDITING_ONBOARDING_GENERATION_STARTED,
  void
> = createAction(VISUAL_EDITING_ONBOARDING_GENERATION_STARTED);

export const visualEditingOnboardingStarted: ActionCreator<
  typeof VISUAL_EDITING_ONBOARDING_STARTED,
  void
> = createAction(VISUAL_EDITING_ONBOARDING_STARTED);

/**
 * Instruction source-file sync action creators
 */
export const instructionSourceFileUpserted: ActionCreator<
  typeof INSTRUCTION_SOURCE_FILE_UPSERTED,
  DesignerSourceFile
> = createAction(INSTRUCTION_SOURCE_FILE_UPSERTED);

export const vccSourceFileContentSaved: ActionCreator<
  typeof VCC_SOURCE_FILE_CONTENT_SAVED,
  DesignerSourceFile
> = createAction(VCC_SOURCE_FILE_CONTENT_SAVED);

export type VccSourceFileCreatedPayload = {name: string; content: string};
export const vccSourceFileCreated: ActionCreator<
  typeof VCC_SOURCE_FILE_CREATED,
  VccSourceFileCreatedPayload
> = createAction(VCC_SOURCE_FILE_CREATED);

/** `key` identifies the record; `name` is its new path-qualified name. */
export type VccSourceFileRenamedPayload = {key: string; name: string};
export const vccSourceFileRenamed: ActionCreator<
  typeof VCC_SOURCE_FILE_RENAMED,
  VccSourceFileRenamedPayload
> = createAction(VCC_SOURCE_FILE_RENAMED);

export type VccSourceFileDeletedPayload = {key: string};
export const vccSourceFileDeleted: ActionCreator<
  typeof VCC_SOURCE_FILE_DELETED,
  VccSourceFileDeletedPayload
> = createAction(VCC_SOURCE_FILE_DELETED);

export type VccUnusedSourceFilesCollectedPayload = {keys: readonly string[]};
export const vccUnusedSourceFilesCollected: ActionCreator<
  typeof VCC_UNUSED_SOURCE_FILES_COLLECTED,
  VccUnusedSourceFilesCollectedPayload
> = createAction(VCC_UNUSED_SOURCE_FILES_COLLECTED);

/**
 * An instruction file as reported by the list endpoint, which omits content.
 */
export type ReconciledInstructionSourceFile = Omit<
  DesignerSourceFile,
  'content'
>;

export const instructionSourceFilesReconciled: ActionCreator<
  typeof INSTRUCTION_SOURCE_FILES_RECONCILED,
  {files: ReconciledInstructionSourceFile[]}
> = createAction(INSTRUCTION_SOURCE_FILES_RECONCILED);

export const instructionSubtreeRenamed: ActionCreator<
  typeof INSTRUCTION_SUBTREE_RENAMED,
  {
    from: string;
    to: string;
    previous?: SourceFileKey;
    instruction?: DesignerSourceFile;
  }
> = createAction(INSTRUCTION_SUBTREE_RENAMED);

export const instructionSubtreeDeleted: ActionCreator<
  typeof INSTRUCTION_SUBTREE_DELETED,
  {root: string; files: SourceFileKey[]}
> = createAction(INSTRUCTION_SUBTREE_DELETED);

export type CodeLibrariesActions =
  | ReturnType<typeof localDevServerReachabilityChanged>
  | ReturnType<typeof localDevLibraryDiscovered>
  | ReturnType<typeof localOverrideOptOutChanged>
  | ReturnType<typeof codeComponentSourceDraftChanged>
  | ReturnType<typeof codeComponentSourceDraftsCleared>
  | ReturnType<typeof codeComponentSourceFileSaveRefused>
  | ReturnType<typeof codeComponentEditorPaneShow>
  | ReturnType<typeof codeComponentEditorPaneHide>
  | ReturnType<typeof codeComponentEditorPaneMove>
  | ReturnType<typeof codeComponentEditorPaneResize>
  | ReturnType<typeof codeComponentSourceFileSelected>
  | ReturnType<typeof codeComponentSourceTreeCollapsed>
  | ReturnType<typeof codeComponentCreated>
  | ReturnType<typeof codeComponentTemplateDragStarted>
  | ReturnType<typeof codeComponentTemplateWelcomeShown>
  | ReturnType<typeof codeComponentUpdated>
  | ReturnType<typeof codeEvaluated>
  | ReturnType<typeof codeEvaluationFailed>
  | ReturnType<typeof componentPreviewScenariosDefined>
  | ReturnType<typeof componentPreviewScenarioRemoved>
  | ReturnType<typeof componentPreviewScenarioEdited>
  | ReturnType<typeof componentPreviewScenarioCreated>
  | ReturnType<typeof codeComponentReloadedFromCrash>
  | ReturnType<typeof codeBuildStarted>
  | ReturnType<typeof codeBuildSucceeded>
  | ReturnType<typeof codeBuildFailed>
  | ReturnType<typeof codeDependenciesInstallationFinished>
  | ReturnType<typeof remoteFileSystemFilesCommitted>
  | ReturnType<typeof codeComponentCreatorDismissed>
  | ReturnType<typeof codeComponentCreatorPromptSet>
  | ReturnType<typeof codeComponentClarificationStarted>
  | ReturnType<typeof codeComponentClarificationEnded>
  | ReturnType<typeof vccStyleOverridesUpdated>
  | ReturnType<typeof vccStyleOverridesCleared>
  | ReturnType<typeof vccStyleOverrideGroupRemoved>
  | ReturnType<typeof vccStyleOverridesIdentityDropped>
  | ReturnType<typeof vccStyleOverridesInstantApplied>
  | ReturnType<typeof vccStyleOverridesInstantApplyPending>
  | ReturnType<typeof vccSelectedElementRevealed>
  | ReturnType<typeof vccDraftStylesApplied>
  | ReturnType<typeof vccPropConnectionsResolved>
  | ReturnType<typeof vccTextOverridesUpdated>
  | ReturnType<typeof vccTextOverridesCleared>
  | ReturnType<typeof vccTextOverrideGroupRemoved>
  | ReturnType<typeof vccTextEditStarted>
  | ReturnType<typeof vccTextEditEnded>
  | ReturnType<typeof vccInstantTextApplied>
  | ReturnType<typeof vccInstantApplyReported>
  | ReturnType<typeof vccInstantApplyRolledBack>
  | ReturnType<typeof vccApplyCancelled>
  | ReturnType<typeof vccApplySourceSnapshotTaken>
  | ReturnType<typeof vccApplySourceSnapshotDropped>
  | ReturnType<typeof vccApplyUndoTurnAttached>
  | ReturnType<typeof visualEditingOnboardingAdvanced>
  | ReturnType<typeof visualEditingOnboardingDismissed>
  | ReturnType<typeof visualEditingOnboardingGenerationStarted>
  | ReturnType<typeof visualEditingOnboardingStarted>
  | ReturnType<typeof aiAssistantOpened>
  | ReturnType<typeof instructionSourceFileUpserted>
  | ReturnType<typeof vccSourceFileContentSaved>
  | ReturnType<typeof vccSourceFileCreated>
  | ReturnType<typeof vccSourceFileRenamed>
  | ReturnType<typeof vccSourceFileDeleted>
  | ReturnType<typeof instructionSourceFilesReconciled>
  | ReturnType<typeof instructionSubtreeRenamed>
  | ReturnType<typeof instructionSubtreeDeleted>;

export const remoteSandboxCreated: ActionCreator<
  typeof REMOTE_SANDBOX_CREATED,
  CreateSandboxResponse
> = createAction(REMOTE_SANDBOX_CREATED);

export const remoteSandboxInitiated: ActionCreator<
  typeof REMOTE_SANDBOX_INITIATED,
  void
> = createAction(REMOTE_SANDBOX_INITIATED);

export const remoteSandboxSyncStarted: ActionCreator<
  typeof REMOTE_SANDBOX_SYNC_STARTED,
  string[]
> = createAction(REMOTE_SANDBOX_SYNC_STARTED);

export const remoteSandboxSyncCompleted: ActionCreator<
  typeof REMOTE_SANDBOX_SYNC_COMPLETED,
  string[]
> = createAction(REMOTE_SANDBOX_SYNC_COMPLETED);

export const remoteSandboxExpired: ActionCreator<
  typeof REMOTE_SANDBOX_EXPIRED,
  void
> = createAction(REMOTE_SANDBOX_EXPIRED);

/**
 * Dispatched by the engine after it removes component files from the remote
 * sandbox (their components were deleted in the Designer). The payload is the
 * list of filesystem paths that were removed, so the store can drop them from
 * its `PENDING_DELETE` tracking.
 */
export const remoteSandboxFilesDeleted: ActionCreator<
  typeof REMOTE_SANDBOX_FILES_DELETED,
  string[]
> = createAction(REMOTE_SANDBOX_FILES_DELETED);

export type RemoteFileSystemFilesCommittedPayload = {
  committed: Record<string, string>;
  deleted: string[];
};

export const remoteFileSystemFilesCommitted: ActionCreator<
  typeof REMOTE_FILESYSTEM_FILES_COMMITTED,
  RemoteFileSystemFilesCommittedPayload
> = createAction(REMOTE_FILESYSTEM_FILES_COMMITTED);

export const functionsGenerationMissing: ActionCreator<
  typeof FUNCTIONS_GENERATION_MISSING,
  {functionsHash: string}
> = createAction(FUNCTIONS_GENERATION_MISSING);

export const localDevServerReachabilityChanged: ActionCreator<
  typeof LOCAL_DEV_SERVER_REACHABILITY_CHANGED,
  {isReachable: boolean}
> = createAction(LOCAL_DEV_SERVER_REACHABILITY_CHANGED);

export const localDevLibraryDiscovered: ActionCreator<
  typeof LOCAL_DEV_LIBRARY_DISCOVERED,
  {descriptor: LocalLibraryDescriptor | null}
> = createAction(LOCAL_DEV_LIBRARY_DISCOVERED);

export const localOverrideAdded: ActionCreator<
  typeof LOCAL_OVERRIDE_ADDED,
  {
    source: LocalOverrideSource;
    plugin: Plugins.PluginDeclarations;
  }
> = createAction(LOCAL_OVERRIDE_ADDED);

export const localOverrideRemoved: ActionCreator<
  typeof LOCAL_OVERRIDE_REMOVED,
  {id: string}
> = createAction(LOCAL_OVERRIDE_REMOVED);

export const localOverrideOptOutChanged: ActionCreator<
  typeof LOCAL_OVERRIDE_OPT_OUT_CHANGED,
  {librarySlug: string; isOptedOut: boolean}
> = createAction(LOCAL_OVERRIDE_OPT_OUT_CHANGED);

export type RemoteSandboxActions =
  | ReturnType<typeof remoteSandboxCreated>
  | ReturnType<typeof remoteSandboxInitiated>
  | ReturnType<typeof remoteSandboxSyncStarted>
  | ReturnType<typeof remoteSandboxSyncCompleted>
  | ReturnType<typeof remoteSandboxExpired>
  | ReturnType<typeof remoteSandboxFilesDeleted>
  | ReturnType<typeof remoteFileSystemFilesCommitted>
  | ReturnType<typeof codeBuildSucceeded>
  | ReturnType<typeof codeBuildStarted>
  | ReturnType<typeof codeBuildFailed>
  | ReturnType<typeof codeDependenciesInstallationFinished>
  // The store skips the rebuild for a rename that cannot change the bundle.
  | ReturnType<typeof vccSourceFileRenamed>
  | ReturnType<typeof functionsGenerationMissing>
  | SitePluginActions;

export const functionsPreviewConnecting: ActionCreator<
  typeof FUNCTIONS_PREVIEW_CONNECTING,
  {moduleKey: string}
> = createAction(FUNCTIONS_PREVIEW_CONNECTING);

export const functionsPreviewConnected: ActionCreator<
  typeof FUNCTIONS_PREVIEW_CONNECTED,
  {moduleKey: string; preview: FunctionsPreview}
> = createAction(FUNCTIONS_PREVIEW_CONNECTED);

export const functionsPreviewFailed: ActionCreator<
  typeof FUNCTIONS_PREVIEW_FAILED,
  {moduleKey: string; attempts: number; retryAt: number | null}
> = createAction(FUNCTIONS_PREVIEW_FAILED);

export const functionsPreviewEmpty: ActionCreator<
  typeof FUNCTIONS_PREVIEW_EMPTY,
  {moduleKey: string}
> = createAction(FUNCTIONS_PREVIEW_EMPTY);

export const functionsPreviewDisabled: ActionCreator<
  typeof FUNCTIONS_PREVIEW_DISABLED,
  void
> = createAction(FUNCTIONS_PREVIEW_DISABLED);

export type FunctionsSandboxActions =
  | ReturnType<typeof functionsPreviewConnecting>
  | ReturnType<typeof functionsPreviewConnected>
  | ReturnType<typeof functionsPreviewFailed>
  | ReturnType<typeof functionsPreviewEmpty>
  | ReturnType<typeof functionsPreviewDisabled>
  | ReturnType<typeof functionsGenerationMissing>
  | ReturnType<typeof codeBuildStarted>
  | ReturnType<typeof codeBuildSucceeded>
  | ReturnType<typeof codeBuildFailed>;

export type RemoteFile = {
  source: string;
  /**
   * `PENDING_DELETE` marks a component file that's still on the remote sandbox
   * but no longer in the local projection (its component was deleted in the
   * Designer). The engine removes such files from the sandbox and then drops
   * them from state via `remoteSandboxFilesDeleted`.
   */
  status: 'PENDING' | 'SYNCING' | 'SYNCED' | 'PENDING_DELETE';
};
export type SandboxFileSystem = {[path: string]: RemoteFile};
